// src/components/HousingEra.jsx (s110) — PHASE 3B-3a: how old the housing stock is,
// from Census ACS year-built counts (src/data/housingEra.js, gated by verify-housing-era).
// Data-dense on purpose: the numbers are the city's own; the only shared text is short
// legend notes, so the section adds unique material without adding boilerplate prose.
import React from 'react';
import { housingEras, fmtPct } from './housingEras.js';

export default function HousingEra({ housing, T, fontDisplay, fontMono, fontBody, headingId }) {
  if (!housing) return null;
  const { units, median, place, geo } = housing;
  const eras = housingEras(housing);
  const single = !geo.includes('+');
  const tableUrl = single ? `https://data.census.gov/table/ACSDT5Y2024.B25034?g=${geo.slice(0, 3)}XX00US${geo.slice(9)}` : null;
  const stat = (label, value, small) => (
    <div>
      <div style={{ fontFamily: fontMono, fontSize: 9, letterSpacing: '0.18em', color: T.inkFaint, marginBottom: 6 }}>{label}</div>
      <div data-stat={label} style={{ fontFamily: fontDisplay, fontSize: small ? 18 : 28, fontWeight: 500, color: T.ink, lineHeight: 1.1 }}>{value}</div>
    </div>
  );
  return (
    <div data-region="housing-era" style={{ marginTop: 56 }}>
      <div style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em', color: T.sienna, marginBottom: 14 }}>HOUSING STOCK</div>
      <h2 id={headingId} style={{ fontFamily: fontDisplay, fontSize: 28, fontWeight: 400, lineHeight: 1.15, letterSpacing: '-0.02em', margin: '0 0 18px', color: T.ink }}>How old the glass is</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 18, marginBottom: 18 }}>
        {median !== null && stat('MEDIAN YEAR BUILT', String(median), typeof median !== 'number')}
        {stat('BUILT BEFORE 1940', fmtPct(eras[0].p))}
        {stat('BUILT 2010 OR LATER', fmtPct(eras[5].p))}
        {stat('HOUSING UNITS', units.toLocaleString('en-US'))}
      </div>
      <div role="img" aria-label={eras.map((e) => `${e.label}: ${fmtPct(e.p)}`).join(', ')}
        style={{ display: 'flex', height: 18, border: `1px solid ${T.rule}`, overflow: 'hidden', marginBottom: 14 }}>
        {eras.map((e) => <div key={e.label} data-era={e.label} style={{ width: `${e.p}%`, background: T.sienna, opacity: e.shade }} />)}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px 18px' }}>
        {eras.map((e) => (
          <li key={e.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span aria-hidden="true" style={{ width: 12, height: 12, marginTop: 3, background: T.sienna, opacity: e.shade, flex: '0 0 auto' }} />
            <span>
              <span style={{ display: 'block', fontFamily: fontMono, fontSize: 11, color: T.ink }}>{e.label} · {fmtPct(e.p)}</span>
              <span style={{ display: 'block', fontFamily: fontBody, fontSize: 13, color: T.inkFaint }}>{e.note}</span>
            </span>
          </li>
        ))}
      </ul>
      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.04em', color: T.inkFaint, marginTop: 14 }}>
        <span style={{ display: 'block' }}>
          {tableUrl ? <a href={tableUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>US Census · ACS 2020–2024 · B25034</a> : 'US Census · ACS 2020–2024 · B25034'}
        </span>
        <span style={{ display: 'block' }}>Geography: {place}</span>
      </div>
    </div>
  );
}
